import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle,
    ArrowLeft,
    Check,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    FileDown,
    Lightbulb,
    Loader2,
    MessageCircle,
    Phone,
    PhoneOff,
    Sparkles,
    Target,
    Undo2,
    UserX,
    X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import ScoreRing from "../components/candidates/ScoreRing";
import SmartBadge from "../components/candidates/SmartBadge";
import ConfirmModal from "../components/common/ConfirmModal";
import SendMessageModal from "../components/messaging/SendMessageModal";
import { api } from "../lib/api";
import { getQueuePosition } from "../lib/callQueue";
import { PROFILE_META } from "../lib/constants";

/**
 * Focus Mode — high-velocity calling layout.
 *
 * No sidebar, no topbar — just the essentials a pre-recruiter needs to make
 * a call decision in 10 seconds:
 *   • identity + phone (one-click dial)
 *   • AI score + top badges + dominant profile
 *   • 3 strengths, 3 vigilance, 3 priority questions
 *   • Quick actions: Open scorecard / No-answer / Reject
 *   • ← / → through the queue, Esc to exit
 */
export default function CandidateFocus() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [acting, setActing] = useState(false);
    // Confirmation banner shown right after a "Pas de réponse" / "Refuser" action.
    // Contains: { kind: 'no_answer'|'rejected', attempts: int }
    const [actionFeedback, setActionFeedback] = useState(null);
    // Reject confirmation modal
    const [confirmReject, setConfirmReject] = useState(false);
    // Reset-all-attempts confirmation modal
    const [confirmResetAttempts, setConfirmResetAttempts] = useState(false);
    // SMS / WhatsApp messaging
    const [messagingState, setMessagingState] = useState(null); // {category} | null

    const queueInfo = useMemo(() => getQueuePosition(id), [id]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/candidates/${id}`);
            setCandidate(data.candidate);
            setError(null);
        } catch (e) {
            setError(e.response?.status === 403 ? "Hors périmètre" : "Candidat introuvable");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);
    // Reset feedback banner when the candidate changes.
    useEffect(() => { setActionFeedback(null); }, [id]);

    // Offline-friendly preload: warm the cache of the next 3 candidates so the
    // operator can keep navigating ← / → even if the network drops mid-session.
    useEffect(() => {
        if (!queueInfo) return;
        const targets = [queueInfo.nextId, queueInfo.prevId];
        const queue = queueInfo.allIds || [];
        const pos = queueInfo.position - 1;
        for (let i = 1; i <= 3; i += 1) {
            if (queue[pos + i]) targets.push(queue[pos + i]);
        }
        const unique = [...new Set(targets.filter(Boolean))];
        unique.forEach((nid) => {
            // Fire-and-forget; cached in localStorage by the api interceptor
            api.get(`/candidates/${nid}`).catch(() => { /* ignore */ });
        });
    }, [queueInfo]);

    // Mark + offline-queueable mutation flag (factorised — these all do the
    // same opt-in pattern so the action survives a network drop).
    const queueable = (label) => ({
        offlineQueueable: true,
        offlineLabel: `${label} · ${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim(),
    });

    const goPrev = useCallback(() => {
        if (queueInfo?.prevId) navigate(`/candidats/${queueInfo.prevId}/focus`);
    }, [navigate, queueInfo]);
    const goNext = useCallback(() => {
        if (queueInfo?.nextId) navigate(`/candidats/${queueInfo.nextId}/focus`);
    }, [navigate, queueInfo]);
    const exitFocus = useCallback(() => {
        navigate(`/candidats/${id}`);
    }, [navigate, id]);

    // Keyboard shortcuts ← / → and Esc + M for messaging
    useEffect(() => {
        function handleKey(e) {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
            else if (e.key === "Escape") exitFocus();
            else if (e.key === "m" || e.key === "M") {
                if (candidate?.phone) setMessagingState({ category: "custom" });
            }
        }
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [goPrev, goNext, exitFocus, candidate?.phone]);

    async function markNoAnswer() {
        if (!candidate || acting) return;
        setActing(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/no-answer`, undefined, {
                ...queueable("Pas de réponse"),
                optimisticData: { ...candidate, call_attempts: (candidate.call_attempts || 0) + 1, queuedOffline: true },
            });
            // Update local candidate to reflect new attempt count + status
            setCandidate(data);
            setActionFeedback({ kind: "no_answer", attempts: data.call_attempts || 0 });
            // Auto-suggest sending the "Pas de réponse" SMS to the candidate.
            if (data.phone) {
                setMessagingState({ category: "no_answer" });
            }
        } catch {
            toast.error("Erreur — réessayez");
        } finally {
            setActing(false);
        }
    }

    async function undoNoAnswer() {
        if (!candidate || acting) return;
        setActing(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/undo-no-answer`);
            setCandidate(data);
            setActionFeedback(null);
            toast.success("Action annulée");
        } catch {
            toast.error("Impossible d'annuler — réessayez");
        } finally {
            setActing(false);
        }
    }

    async function confirmRejectAction() {
        if (!candidate || acting) return;
        setConfirmReject(false);
        setActing(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/reject`, undefined, queueable("Refus"));
            setCandidate(data);
            setActionFeedback({ kind: "rejected" });
        } catch {
            toast.error("Erreur — réessayez");
        } finally {
            setActing(false);
        }
    }

    async function undoReject() {
        if (!candidate || acting) return;
        setActing(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/revert-status`);
            setCandidate(data);
            setActionFeedback(null);
            toast.success("Refus annulé");
        } catch {
            toast.error("Impossible d'annuler — réessayez");
        } finally {
            setActing(false);
        }
    }

    async function resetAllAttempts() {
        if (!candidate || acting) return;
        setConfirmResetAttempts(false);
        setActing(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/reset-call-attempts`);
            setCandidate(data);
            setActionFeedback(null);
            toast.success("Toutes les tentatives ont été annulées");
        } catch {
            toast.error("Impossible de réinitialiser — réessayez");
        } finally {
            setActing(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-shariti-cream flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
            </div>
        );
    }

    if (error || !candidate) {
        return (
            <div className="min-h-screen bg-shariti-cream flex items-center justify-center p-6">
                <div className="card-soft p-8 text-center max-w-md">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
                    <h2 className="mt-3 text-xl font-black text-shariti-black">{error || "Erreur"}</h2>
                    <button onClick={exitFocus} className="mt-5 btn-shariti">
                        <ArrowLeft className="h-4 w-4" /> Retour
                    </button>
                </div>
            </div>
        );
    }

    const c = candidate;
    const ai = c.ai_analysis || {};
    const strengths = (ai.strengths || []).slice(0, 4);
    const vigilance = (ai.vigilance || []).slice(0, 4);
    const questions = (ai.priority_questions || []).slice(0, 4);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-screen bg-gradient-to-br from-shariti-cream via-shariti-cream to-shariti-orange-soft/30"
            data-testid="candidate-focus"
        >
            {/* Top bar — minimal */}
            <header className="sticky top-0 z-20 backdrop-blur-md bg-white/80 border-b border-shariti-gray">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
                    <button
                        onClick={exitFocus}
                        data-testid="focus-exit"
                        title="Quitter le mode Focus (Esc)"
                        className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full bg-shariti-cream border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange transition text-[11px] font-extrabold"
                    >
                        <X className="h-4 w-4" />
                        Quitter le focus
                    </button>
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest shadow-btn">
                        <Target className="h-3 w-3" /> Mode Focus
                    </div>
                    {queueInfo && (
                        <div className="ml-auto flex items-center gap-2 md:gap-3 rounded-full bg-white border border-shariti-orange/20 pl-3 pr-1.5 py-1">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange truncate max-w-[200px]">
                                {queueInfo.label}
                            </div>
                            <div className="text-xs font-extrabold text-shariti-black">
                                {queueInfo.position} / {queueInfo.total}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={goPrev}
                                    disabled={!queueInfo.prevId}
                                    data-testid="focus-prev"
                                    title="Précédent (←)"
                                    className="h-8 px-3 inline-flex items-center gap-1 rounded-full bg-white border border-shariti-orange/30 text-shariti-orange hover:bg-shariti-orange hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold"
                                >
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={goNext}
                                    disabled={!queueInfo.nextId}
                                    data-testid="focus-next"
                                    title="Suivant (→)"
                                    className="h-8 px-3 inline-flex items-center gap-1 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold shadow-btn"
                                >
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main */}
            <main className="max-w-6xl mx-auto px-6 py-6 md:py-10">
                <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="card-soft p-6 md:p-10"
                >
                    {/* Action feedback banner — appears after Pas de réponse / Refuser. Lets user undo or advance. */}
                    <AnimatePresence>
                        {actionFeedback && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, height: 0 }}
                                animate={{ opacity: 1, y: 0, height: "auto" }}
                                exit={{ opacity: 0, y: -10, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className={`mb-6 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-3 ${
                                    actionFeedback.kind === "no_answer"
                                        ? "bg-amber-50 border-2 border-amber-200"
                                        : "bg-red-50 border-2 border-red-200"
                                }`}
                                data-testid="focus-action-feedback"
                            >
                                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                                    actionFeedback.kind === "no_answer" ? "bg-amber-500 text-white" : "bg-shariti-red text-white"
                                }`}>
                                    <Check className="h-5 w-5" strokeWidth={3} />
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <div className={`text-sm font-extrabold ${actionFeedback.kind === "no_answer" ? "text-amber-900" : "text-red-900"}`}>
                                        {actionFeedback.kind === "no_answer"
                                            ? `Marqué : pas de réponse · ${actionFeedback.attempts} tentative${actionFeedback.attempts > 1 ? "s" : ""}`
                                            : `Candidat refusé`}
                                    </div>
                                    <div className={`text-[11px] font-medium ${actionFeedback.kind === "no_answer" ? "text-amber-700" : "text-red-700"} mt-0.5`}>
                                        {actionFeedback.kind === "no_answer"
                                            ? "Le candidat reste dans votre file pour un rappel. Cliquez « Annuler » si c'était une erreur."
                                            : "Le candidat ne sera plus visible dans le pipeline. Cliquez « Annuler » si c'était une erreur."}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={actionFeedback.kind === "no_answer" ? undoNoAnswer : undoReject}
                                        disabled={acting}
                                        data-testid="focus-action-undo"
                                        className="inline-flex items-center gap-1.5 bg-white border-2 border-shariti-gray text-slate-700 hover:border-shariti-black hover:text-shariti-black rounded-full font-extrabold px-4 h-9 text-[11px] uppercase tracking-widest transition disabled:opacity-50"
                                    >
                                        <Undo2 className="h-3.5 w-3.5" /> Annuler
                                    </button>
                                    {queueInfo?.nextId && (
                                        <button
                                            onClick={goNext}
                                            data-testid="focus-action-next"
                                            className="inline-flex items-center gap-1.5 bg-shariti-orange hover:bg-[#d66528] text-white rounded-full font-extrabold px-4 h-9 text-[11px] uppercase tracking-widest transition shadow-btn"
                                        >
                                            Suivant <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Hero */}
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        {c.avatar_url ? (
                            <img src={c.avatar_url} alt="" className="h-24 w-24 md:h-32 md:w-32 rounded-3xl object-cover shrink-0" />
                        ) : (
                            <div className="h-24 w-24 md:h-32 md:w-32 rounded-3xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-4xl flex items-center justify-center shrink-0">
                                {c.first_name?.[0]}{c.last_name?.[0]}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-shariti-black leading-tight">
                                {c.first_name} {c.last_name}
                            </h1>
                            <div className="mt-2 text-sm font-bold text-slate-500">
                                {c.position} · {c.city || "—"} · {c.source}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {(c.badges || []).slice(0, 4).map((b) => <SmartBadge key={b} type={b} size="sm" />)}
                            </div>
                        </div>
                        <div className="shrink-0 flex flex-col items-center gap-2">
                            <ScoreRing score={c.ai_score} size={128} stroke={8} />
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                {PROFILE_META[c.profile_dominant] || "Profil à déterminer"}
                            </div>
                        </div>
                    </div>

                    {/* Phone action — hero CTA */}
                    {c.phone && (
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <a
                                href={`tel:${c.phone}`}
                                data-testid="focus-call"
                                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-black px-6 py-3 text-lg transition-all active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.35)]"
                            >
                                <Phone className="h-5 w-5" />
                                {c.phone}
                            </a>
                            {c.cv_url && (
                                <a
                                    href={c.cv_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="focus-cv"
                                    className="inline-flex items-center gap-2 bg-shariti-black hover:bg-slate-800 text-white rounded-full font-bold px-5 py-2.5 text-sm transition"
                                >
                                    <FileDown className="h-4 w-4" /> CV PDF
                                </a>
                            )}
                            <button
                                onClick={() => navigate(`/candidats/${c.id}/scorecard/prerec`)}
                                data-testid="focus-scorecard"
                                className="btn-shariti"
                            >
                                <ClipboardList className="h-4 w-4" /> Lancer la scorecard
                            </button>
                            <button
                                onClick={() => setMessagingState({ category: "custom" })}
                                disabled={!c.phone}
                                data-testid="focus-send-message"
                                title="Envoyer un message (M)"
                                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold px-5 py-2.5 text-sm transition disabled:opacity-50 shadow-btn"
                            >
                                <MessageCircle className="h-4 w-4" /> Envoyer un message
                            </button>
                            <button
                                onClick={markNoAnswer}
                                disabled={acting}
                                data-testid="focus-no-answer"
                                className="inline-flex items-center gap-2 bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 rounded-full font-bold px-5 py-2.5 text-sm transition disabled:opacity-50"
                            >
                                <PhoneOff className="h-4 w-4" /> Pas de réponse
                            </button>
                            <button
                                onClick={() => setConfirmReject(true)}
                                disabled={acting}
                                data-testid="focus-reject"
                                className="inline-flex items-center gap-2 bg-white border border-red-200 text-shariti-red hover:bg-red-50 rounded-full font-bold px-5 py-2.5 text-sm transition disabled:opacity-50"
                            >
                                <UserX className="h-4 w-4" /> Refuser
                            </button>
                            {c.call_attempts > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setConfirmResetAttempts(true)}
                                    data-testid="focus-reset-attempts"
                                    title="Annuler toutes les tentatives — remet le candidat à zéro"
                                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-extrabold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-400 px-3 py-1 rounded-full transition group"
                                >
                                    <span>{c.call_attempts} tentative{c.call_attempts > 1 ? "s" : ""} sans réponse</span>
                                    <Undo2 className="h-3 w-3 opacity-60 group-hover:opacity-100 transition" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* AI summary block */}
                    {ai.summary && (
                        <div className="mt-8 p-5 rounded-2xl bg-shariti-orange-soft/40 border border-shariti-orange/20">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="h-4 w-4 text-shariti-orange" />
                                <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange">
                                    Synthèse IA · Recommandation
                                </div>
                            </div>
                            <p className="text-sm font-bold text-shariti-black leading-relaxed">{ai.summary}</p>
                            {ai.recommendation && (
                                <p className="mt-2 text-xs font-extrabold text-shariti-orange">→ {ai.recommendation}</p>
                            )}
                        </div>
                    )}

                    {/* 3 columns: strengths / vigilance / priority Qs */}
                    <div className="mt-6 grid md:grid-cols-3 gap-4">
                        <Block title="Points forts" color="text-emerald-700" border="border-emerald-200" bg="bg-emerald-50/40" items={strengths} />
                        <Block title="Vigilance" color="text-amber-700" border="border-amber-200" bg="bg-amber-50/40" items={vigilance} />
                        <Block title="Questions prioritaires" color="text-shariti-orange" border="border-shariti-orange/20" bg="bg-shariti-orange-soft/30" items={questions} icon={Lightbulb} />
                    </div>
                </motion.div>

                {/* Keyboard hints */}
                <div className="mt-6 text-center text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    <kbd className="px-2 py-1 rounded bg-white border border-shariti-gray font-extrabold mr-1">←</kbd>
                    Précédent
                    <span className="mx-3 text-slate-300">·</span>
                    <kbd className="px-2 py-1 rounded bg-white border border-shariti-gray font-extrabold mr-1">→</kbd>
                    Suivant
                    <span className="mx-3 text-slate-300">·</span>
                    <kbd className="px-2 py-1 rounded bg-white border border-shariti-gray font-extrabold mr-1">M</kbd>
                    Message
                    <span className="mx-3 text-slate-300">·</span>
                    <kbd className="px-2 py-1 rounded bg-white border border-shariti-gray font-extrabold mr-1">Esc</kbd>
                    Quitter
                </div>
            </main>

            {confirmReject && (
                <ConfirmModal
                    title="Refuser définitivement ce candidat ?"
                    body={(
                        <>
                            Le candidat <strong>{c.first_name} {c.last_name}</strong> passera au statut <strong>« Refusé »</strong> et ne sera plus visible dans le pipeline. Cette action peut être annulée tant que vous restez sur cette page.
                        </>
                    )}
                    confirmLabel="Refuser le candidat"
                    tone="red"
                    icon={UserX}
                    onCancel={() => setConfirmReject(false)}
                    onConfirm={confirmRejectAction}
                />
            )}

            {confirmResetAttempts && (
                <ConfirmModal
                    title="Annuler toutes les tentatives ?"
                    body={(
                        <>
                            Toutes les <strong>{c.call_attempts} tentatives</strong> sans réponse pour <strong>{c.first_name} {c.last_name}</strong> seront effacées. Le candidat sera de nouveau considéré comme jamais appelé et reviendra dans votre file initiale.
                        </>
                    )}
                    confirmLabel="Tout annuler"
                    tone="orange"
                    icon={Undo2}
                    onCancel={() => setConfirmResetAttempts(false)}
                    onConfirm={resetAllAttempts}
                />
            )}

            {messagingState && (
                <SendMessageModal
                    candidate={c}
                    defaultCategory={messagingState.category}
                    onClose={() => setMessagingState(null)}
                    onSent={() => { /* history shows in profile page */ }}
                />
            )}
        </motion.div>
    );
}

function Block({ title, color, border, bg, items, icon: Icon }) {
    if (!items || items.length === 0) return null;
    return (
        <div className={`rounded-2xl border ${border} ${bg} p-4`}>
            <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest mb-2 ${color}`}>
                {Icon && <Icon className="h-3 w-3" />}
                {title}
            </div>
            <ul className="space-y-1.5 text-sm font-semibold text-slate-700">
                {items.map((it, i) => (
                    <li key={i} className="flex gap-2 leading-snug">
                        <span className="text-slate-300">·</span> {it}
                    </li>
                ))}
            </ul>
        </div>
    );
}
